import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ComposerPostPreview from 'flarum/forum/components/ComposerPostPreview';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import ComposerState from 'flarum/forum/states/ComposerState';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import username from 'flarum/common/helpers/username';
import { truncate } from 'flarum/common/utils/string';
import TextEditor from 'flarum/common/components/TextEditor';
import GuestReplyCta from './components/GuestReplyCta';
/**
 * Replying inside a discussion happens in the page itself: under the last post there is always
 * a "НАПИСАТЬ ОТВЕТ" form instead of a field that opens the floating composer.
 *
 * There is still one composer state (app.composer) and one editor: the form is core's
 * ReplyComposer — TextEditor, toolbar, uploads, mentions, validation, submission — loaded into
 * app.composer as usual and rendered in the post stream's reply slot (where ReplyPlaceholder was)
 * instead of in the floating shell. A body loaded that way carries `tpInline`; for it
 *   - the composer state "shows" it in place (position INLINE) and never mounts the floating shell,
 *   - the floating shell renders nothing and stays hidden,
 *   - minimise / full screen do not apply.
 * Starting a discussion and editing a post keep the floating composer. While it is busy, the reply
 * text waits in memory and comes back into the form afterwards (also when leaving the page).
 *
 * Replying to a particular post is flarum/mentions' own reply(): the post mention it inserts is
 * what links the reply to the post. The form shows that target ("ОТВЕТ ДЛЯ @BOB ×") while the
 * mention is in the text; × removes it, and the sidebar "Ответить" starts a plain reply.
 */
const INLINE = 'tpInline';
const t = function (key, params) {
  if (params === void 0) {
    params = {};
  }
  return app.translator.trans(`tarotportal-theme.forum.discussion.${key}`, params);
};
const isInline = function (composer) {
  if (composer === void 0) {
    composer = app.composer;
  }
  return !!composer.body?.attrs?.tpInline;
};
const inlineFor = discussion => isInline() && app.composer.composingReplyTo(discussion);
const replyTo = () => app.composer.body?.attrs?.tpReplyTo || null;

/** Reply text kept while the composer is used for something else, by discussion id. */
const drafts = new Map();
function saveDraft(composer) {
  if (composer === void 0) {
    composer = app.composer;
  }
  const discussion = composer.body?.attrs?.discussion;
  if (!discussion) return;
  const content = String(composer.fields.content() || '');
  if (content.trim()) drafts.set(discussion.id(), {
    content,
    replyTo: composer.body.attrs.tpReplyTo || null
  });else drafts.delete(discussion.id());
}

/** The text flarum/mentions inserts for a post ("@"bob"#p12"). */
const mentionOf = post => app.mentionFormats?.mentionable('post')?.replacement(post) ?? null;

/** Takes the mention of the current target out of the text (and the target with it). */
function dropReplyTarget() {
  const post = replyTo();
  const mention = post && mentionOf(post);
  app.composer.body.attrs.tpReplyTo = null;
  if (!mention) return;
  const content = String(app.composer.fields.content() || '');
  const start = content.indexOf(mention);
  if (start < 0) return;
  const end = start + mention.length + (content[start + mention.length] === ' ' ? 1 : 0);
  const editor = app.composer.editor;
  if (editor) editor.insertBetween(start, end, '', false);else app.composer.fields.content(content.slice(0, start) + content.slice(end));
}

/** Loads core's reply form into the composer, marked to be shown in the page. */
async function loadInline(discussion) {
  const draft = drafts.get(discussion.id());
  await app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/ReplyComposer'), {
    user: app.session.user,
    discussion,
    tpInline: true,
    tpDraft: draft?.content,
    tpReplyTo: draft?.replyTo || null
  });

  // load() leaves the composer as it was when the user keeps the text of another form
  if (!isInline() || app.composer.body.attrs.discussion !== discussion) return false;
  drafts.delete(discussion.id());
  app.composer.position = INLINE;
  m.redraw();
  return true;
}
const formElement = () => document.querySelector('.TpInlineReply');
const editorElement = () => document.querySelector('.TpInlineReply .TextEditor-editor');

/** Smooth scroll to the form (unless it is already in view); resolves when the scroll ends. */
function scrollToForm() {
  const form = formElement();
  if (!form) return Promise.resolve();
  const rect = form.getBoundingClientRect();
  const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
  const editor = editorElement()?.getBoundingClientRect();
  if (rect.top >= header && (editor ? editor.bottom : rect.bottom) <= window.innerHeight) return Promise.resolve();
  return new Promise(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scrollend', finish);
      resolve();
    };
    window.addEventListener('scrollend', finish);
    setTimeout(finish, 900); // browsers without scrollend
    form.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  });
}

/**
 * Brings the reader to the form and puts the cursor in it. On phones the field is focused at once,
 * inside the tap (iOS opens the keyboard only then) and the browser brings it into view itself.
 */
async function goToForm(discussion) {
  const phone = app.screen() === 'phone';
  if (phone) editorElement()?.focus();
  const stream = app.current.get('stream');
  if (stream && !stream.viewingEnd()) {
    await stream.goToNumber('reply', true);
    m.redraw.sync();
  }
  if (!inlineFor(discussion) && !(await loadInline(discussion))) return false;
  m.redraw.sync();
  if (!phone) await scrollToForm();
  if (document.activeElement !== editorElement()) editorElement()?.focus({
    preventScroll: !phone
  });
  return true;
}

/**
 * The form at the end of the discussion. It renders the composer's body when that body is this
 * discussion's inline reply; otherwise (the floating composer is busy, or the form is loading)
 * the same place shows a way back to the reply.
 */
class InlineReply extends Component {
  loading = false;
  keys = (() => new WeakMap())();
  counter = 0;
  view() {
    const discussion = this.attrs.discussion;
    const composer = app.composer;
    const active = inlineFor(discussion);
    const target = active ? replyTo() : null;
    return m("section", {
      className: "TpInlineReply",
      "aria-labelledby": "tp-inline-reply-title"
    }, m("h2", {
      className: "TpInlineReply-title TpEyebrow",
      id: "tp-inline-reply-title",
      key: "title"
    }, t('write_reply')), active ? this.form(composer, target) : this.idle(discussion));
  }
  form(composer, target) {
    const Body = composer.body.componentClass;
    const body = composer.body;
    if (!this.keys.has(body)) this.keys.set(body, ++this.counter);

    // keyed by the loaded body: a new reply form never reuses the previous one's editor
    return m("div", {
      className: "TpInlineReply-form",
      key: `form-${this.keys.get(body)}`
    }, target && mentionOf(target) && String(composer.fields.content()).includes(mentionOf(target)) ? m("div", {
      className: "TpReplyTarget"
    }, m("div", {
      className: "TpReplyTarget-text"
    }, m("span", {
      className: "TpReplyTarget-label"
    }, t('reply_to', {
      username: username(target.user())
    })), target.contentPlain?.() ? m("span", {
      className: "TpReplyTarget-excerpt"
    }, "\xAB", truncate(target.contentPlain(), 90), "\xBB") : null), m(Button, {
      className: "Button Button--icon Button--link TpReplyTarget-cancel",
      icon: "fas fa-times",
      "aria-label": t('reply_to_cancel'),
      title: t('reply_to_cancel'),
      onclick: () => dropReplyTarget()
    })) : null, m(Body, Object.assign({}, body.attrs, {
      composer: composer,
      disabled: false,
      onTextEditorBuilt: () => composer.resolveEditorReady()
    })), body.attrs.tpPreview && String(composer.fields.content()).trim() ? m("div", {
      className: "TpInlineReply-preview"
    }, m("div", {
      className: "TpInlineReply-previewHead"
    }, m("span", {
      className: "TpEyebrow TpEyebrow--muted"
    }, t('preview')), m(Button, {
      className: "Button Button--link",
      onclick: () => (body.attrs.tpPreview = false, m.redraw())
    }, t('preview_hide'))), m(ComposerPostPreview, {
      className: "Post-body",
      composer: composer
    })) : null);
  }
  idle(discussion) {
    if (this.loading || !app.composer.isVisible()) return m(LoadingIndicator, {
      key: "loading",
      display: "block",
      size: "small"
    });

    // the floating composer holds another form (a new discussion, an edit): replying replaces it
    return m(Button, {
      key: "resume",
      className: "Button TpInlineReply-resume",
      icon: "fas fa-reply",
      onclick: () => goToForm(discussion)
    }, t('write_reply'));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.ensure();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.ensure();
  }

  /** An empty composer gets this discussion's reply form (no scrolling, no focus). */
  ensure() {
    const composer = app.composer;
    if (this.loading || composer.tpLoading || composer.body?.componentClass || composer.isVisible()) return;
    this.loading = true;
    loadInline(this.attrs.discussion).finally(() => {
      this.loading = false;
      m.redraw();
    });
  }
}

/** "ТЕМА ЗАКРЫТА": the end of a locked discussion for those who cannot reply in it. */
const LockedNotice = () => m("section", {
  className: "TpLockedNotice"
}, m("p", {
  className: "TpEyebrow TpEyebrow--muted"
}, t('locked_title')), m("p", {
  className: "TpLockedNotice-text"
}, t('locked_text')));
export default function addInlineReply() {
  // --- the composer state: an inline body is "shown" in place ---------------------------------
  override(ComposerState.prototype, 'show', function (original) {
    if (!isInline(this)) return original();
    this.position = INLINE;
    m.redraw();
  });
  for (const method of ['minimize', 'fullScreen']) {
    override(ComposerState.prototype, method, function (original) {
      if (!isInline(this)) return original();
    });
  }
  override(ComposerState.prototype, 'isFullScreen', function (original) {
    return isInline(this) ? false : original();
  });

  // Another form replaces the reply: its text is kept for later instead of asking to discard it.
  // While any form is being loaded (core clears the composer first) the page form does not load itself.
  override(ComposerState.prototype, 'load', async function (original, componentClass, attrs) {
    if (attrs === void 0) {
      attrs = {};
    }
    if (isInline(this) && !attrs.tpInline) {
      saveDraft(this);
      this.onExit = null;
    }
    this.tpLoading = (this.tpLoading || 0) + 1;
    try {
      return await original(componentClass, attrs);
    } finally {
      this.tpLoading--;
    }
  });

  // A body restored from memory starts with its text (ComposerBody sets originalContent, i.e. '').
  extend('flarum/forum/components/ComposerBody', 'oninit', function () {
    if (this.attrs.tpDraft) this.composer.fields.content(this.attrs.tpDraft);
  });

  // --- the floating shell leaves inline bodies alone ---------------------------------------
  override('flarum/forum/components/Composer', 'view', function (original) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (!isInline(this.state)) return original(...args);
    const body = this.state.body;
    this.state.body = {
      attrs: {}
    };
    try {
      return original(...args);
    } finally {
      this.state.body = body;
    }
  });
  override('flarum/forum/components/Composer', 'animatePositionChange', function (original) {
    return this.state.position === INLINE ? this.hide() : original();
  });
  override('flarum/forum/components/Composer', 'updateBodyPadding', function (original) {
    if (this.state.position !== INLINE) return original();
    $('#content').css({
      paddingBottom: 0
    });
  });

  // --- reply form: no title line (the form sits in its discussion), preview in place --------
  extend('flarum/forum/components/ReplyComposer', 'headerItems', function (items) {
    if (this.attrs.tpInline && items.has('title')) items.remove('title');
  });
  override('flarum/forum/components/ReplyComposer', 'jumpToPreview', function (original) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }
    if (!this.attrs.tpInline) return original(...args);
    this.attrs.tpPreview = true;
    m.redraw();
  });

  // fof/upload: files dropped anywhere on the form are uploaded (its default target is .Composer)
  if (TextEditor.prototype.fofUploadDragAndDropTarget) {
    override(TextEditor.prototype, 'fofUploadDragAndDropTarget', function (original) {
      return original() || this.$().parents('.TpInlineReply')[0];
    });
  }

  // --- replying -----------------------------------------------------------------------------
  // Every "reply" in a discussion being read goes to the form: core's reply buttons call this with
  // goToLast = true (a plain reply, so a post target is dropped); flarum/mentions calls it without
  // arguments right before inserting its post mention.
  override(DiscussionControls, 'replyAction', function (original, goToLast, forceRefresh) {
    const discussion = this;
    if (!app.session.user || !discussion.canReply() || !app.viewingDiscussion(discussion)) return original(goToLast, forceRefresh);
    if (goToLast && inlineFor(discussion)) dropReplyTarget();
    return goToForm(discussion).then(ok => {
      if (!ok) return Promise.reject();
      if (goToLast && replyTo()) dropReplyTarget();
      return app.composer;
    });
  });

  // "Ответить" under a post: flarum/mentions' reply, with one target at a time — a new target
  // replaces the previous one's mention; the same post again only brings the form back.
  extend(CommentPost.prototype, 'actionItems', function (items) {
    const button = items.has('reply') ? items.get('reply') : null;
    const post = this.attrs.post;
    if (!button?.attrs || !app.session.user) return;
    button.attrs.onclick = () => {
      // flarum/mentions loads after this theme: its reply() is looked up when needed
      const module = flarum.reg.get('flarum-mentions', 'forum/utils/reply');
      const reply = module?.default || module;
      const discussion = post.discussion();
      const mention = mentionOf(post);
      if (inlineFor(discussion) && replyTo() === post && mention && String(app.composer.fields.content()).includes(mention)) {
        return goToForm(discussion);
      }
      if (inlineFor(discussion) && replyTo()) dropReplyTarget();
      return Promise.resolve(reply(post)).then(() => {
        if (inlineFor(discussion)) {
          app.composer.body.attrs.tpReplyTo = post;
          m.redraw();
        }
      }).catch(() => {}); // the reader kept another form open
    };
  });

  // --- the end of the stream ----------------------------------------------------------------
  override('flarum/forum/components/ReplyPlaceholder', 'view', function (original) {
    const discussion = this.attrs.discussion;
    if (!app.session.user) return discussion.isLocked?.() ? LockedNotice() : m(GuestReplyCta, null);
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }
    if (!app.current.matches(DiscussionPage)) return original(...args);
    return m(InlineReply, {
      discussion: discussion
    });
  });

  // Members who cannot reply to a locked discussion get the notice instead of nothing.
  extend('flarum/forum/components/PostStream', 'endItems', function (items) {
    const discussion = this.discussion;
    if (app.session.user && !discussion.canReply() && discussion.isLocked?.()) {
      // keyed like the stream's other items
      items.add('tp-locked', m("div", {
        className: "PostStream-item",
        key: "tp-locked"
      }, LockedNotice()), -100);
    }
  });

  // After a reply is published the form starts empty: no stored text for this discussion.
  override('flarum/forum/components/ReplyComposer', 'onsubmit', function (original) {
    if (this.attrs.tpInline) drafts.delete(this.attrs.discussion.id());
    for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
      args[_key4 - 1] = arguments[_key4];
    }
    return original(...args);
  });

  // Leaving the discussion: the text written so far is kept for when the reader comes back.
  override(DiscussionPage.prototype, 'onremove', function (original, vnode) {
    if (this.discussion && inlineFor(this.discussion)) {
      saveDraft();
      app.composer.hide();
    }
    return original(vnode);
  });
}